import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import {
  Button,
  CssBaseline,
  TextField,
  Link,
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Card,
  CardContent,
  Chip,
  Avatar,
  Alert,
  CircularProgress,
  Divider,
  Skeleton,
  Fade,
  Zoom,
  IconButton,
  Tooltip
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { styled } from '@mui/material/styles';
import {
  Description as DescriptionIcon,
  Download as DownloadIcon,
  Preview as PreviewIcon,
  Print as PrintIcon,
  OpenInNew as OpenInNewIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Home as HomeIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon
} from "@mui/icons-material";
import PrintPDF from "./download_laporan";
import { useLocalStorage } from "../../auth/useLocalStorage";

function Copyright(props) {
  return (
    <Typography
      variant="body2"
      color="#FFFFFF"
      align="center"
      {...props}
    >
      {"Copyright © "}
      <Link color="inherit" href="https://makassarkota.go.id/">
        Pemerintah Kota Makassar
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});


const defaultTheme = createTheme({
  palette: {
    primary: {
      main: '#1dc39f',
      light: '#4dd4b8',
      dark: '#14a085',
    },
    secondary: {
      main: '#8ff9ab',
      light: '#b3fac5',
      dark: '#6bc98a',
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
    },
    error: {
      main: '#f44336',
      light: '#e57373',
      dark: '#d32f2f',
    },
    info: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
    },
    background: {
      default: '#f8fffe',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
    h4: {
      fontWeight: 700,
      fontSize: '2rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.43,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
  },
});

const DaftarLaporan = () => {
  const reportTemplate = useRef(null);
  const [longwis] = useLocalStorage("longwis");
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [error, setError] = useState(null);

  // Default data structure to prevent null/undefined errors
  const defaultData = {
    nama_pic: "Muhammad Rezky",
    kontak_pic: "0895805040352",
    nama_longwis: "Buger",
    kecamatan: "Bontoala",
    kelurahan: "Prang Layang",
    alamat: "Jln. Hj. Manila Dg. Pati No. 32",
    tanggal: "2024-05-29",
    status: "Sedang Dikerjakan",
    status_fisik_cctv: "1",
    status_fisik_nvr: "1",
    status_tampilan_system: "1",
    status_tampilan_layar: "1",
    // Additional safety properties
    DataCCTV: {
      status_fisik: "1",
      brand: "-",
      model: "-",
      ip_address: "-"
    },
    DataNVR: {
      status_fisik: "1",
      brand: "-",
      model: "-",
      ip_address: "-"
    },
    Harddisk: {
      status_fisik: "1",
      kapasitas: "-",
      brand: "-"
    },
    Switch: {
      status_fisik: "1",
      brand: "-",
      model: "-"
    },
    TampilanLayar: {
      status: "1",
      resolusi: "-",
      ukuran: "-"
    },
    TampilanSystem: {
      status: "1",
      os: "-",
      versi: "-"
    }
  };

  // Safely merge data with defaults
  const data = useMemo(() => {
    if (!longwis) return defaultData;
    
    // Deep merge to ensure all nested properties exist
    const mergedData = {
      ...defaultData,
      ...longwis,
      DataCCTV: {
        ...defaultData.DataCCTV,
        ...(longwis.DataCCTV || {})
      },
      DataNVR: {
        ...defaultData.DataNVR,
        ...(longwis.DataNVR || {})
      },
      Harddisk: {
        ...defaultData.Harddisk,
        ...(longwis.Harddisk || {})
      },
      Switch: {
        ...defaultData.Switch,
        ...(longwis.Switch || {})
      },
      TampilanLayar: {
        ...defaultData.TampilanLayar,
        ...(longwis.TampilanLayar || {})
      },
      TampilanSystem: {
        ...defaultData.TampilanSystem,
        ...(longwis.TampilanSystem || {})
      }
    };
    
    return mergedData;
  }, [longwis]);

  const handleDownload = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulate download process
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Open download page
      const downloadWindow = window.open("/#/pic/download/longwis", "_blank");
      if (!downloadWindow) {
        throw new Error("Pop-up blocked. Please allow pop-ups for this site.");
      }
      
      // Success feedback
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to download report");
    } finally {
      setLoading(false);
    }
  }, []);

  const handlePreview = useCallback(() => {
    try {
      const previewWindow = window.open("/#/pic/download/longwis", "_blank");
      if (!previewWindow) {
        setError("Pop-up blocked. Please allow pop-ups for this site.");
      }
    } catch (err) {
      setError("Failed to open preview");
    }
  }, []);

  const handlePrint = useCallback(() => {
    try {
      const printWindow = window.open("/#/pic/download/longwis", "_blank");
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          setTimeout(() => {
            printWindow.print();
          }, 1000);
        });
      } else {
        setError("Pop-up blocked. Please allow pop-ups for this site.");
      }
    } catch (err) {
      setError("Failed to print report");
    }
  }, []);

  const handleBack = useCallback(() => {
    window.history.back();
  }, []);

  const handleRefresh = useCallback(() => {
    setPreviewLoading(true);
    setError(null);
    setTimeout(() => {
      setPreviewLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    // Simulate preview loading
    const timer = setTimeout(() => {
      setPreviewLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case "Selesai":
        return "success";
      case "Sedang Dikerjakan":
        return "warning";
      case "Belum Dikerjakan":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Selesai":
        return <CheckCircleIcon fontSize="small" />;
      case "Sedang Dikerjakan":
        return <WarningIcon fontSize="small" />;
      case "Belum Dikerjakan":
        return <InfoIcon fontSize="small" />;
      default:
        return <InfoIcon fontSize="small" />;
    }
  };
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #e8f5e8 0%, #f0fff0 100%)",
        py: 4
      }}
    >
      <ThemeProvider theme={defaultTheme}>
        <Container maxWidth="lg">
          <CssBaseline />
          
          {/* Back Button */}
          <Fade in={true} timeout={600}>
            <Box sx={{ mb: 2 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'white',
                  },
                }}
              >
                Kembali
              </Button>
            </Box>
          </Fade>
          
          {/* Header Section */}
          <Zoom in={true} timeout={800}>
            <Paper 
              elevation={12}
              sx={{
                p: 4,
                mb: 3,
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(29,195,159,0.1) 0%, rgba(255,255,255,0.9) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(29,195,159,0.2)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #1dc39f 0%, #8ff9ab 100%)',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #1dc39f 0%, #8ff9ab 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 3,
                      boxShadow: '0 8px 32px rgba(29,195,159,0.3)',
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%': {
                          boxShadow: '0 8px 32px rgba(29,195,159,0.3)',
                        },
                        '50%': {
                          boxShadow: '0 8px 32px rgba(29,195,159,0.6)',
                        },
                        '100%': {
                          boxShadow: '0 8px 32px rgba(29,195,159,0.3)',
                        },
                      }
                    }}
                  >
                    <DescriptionIcon sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                  
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      component="h1" 
                      variant="h4" 
                      sx={{ 
                        background: 'linear-gradient(135deg, #1dc39f 0%, #14a085 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontWeight: 800,
                        mb: 1
                      }}
                    >
                      Laporan Longwis
                    </Typography>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: 'text.secondary',
                        fontWeight: 500,
                        opacity: 0.8
                      }}
                    >
                      Preview dan download laporan data longwis
                    </Typography>
                  </Box>
                </Box>

                {/* Refresh Button */}
                <Tooltip title="Refresh Preview">
                  <IconButton
                    onClick={handleRefresh}
                    sx={{
                      backgroundColor: 'rgba(29,195,159,0.1)',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                        color: 'white',
                      },
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Error Alert */}
              {error && (
                <Fade in={true}>
                  <Alert 
                    severity="error" 
                    onClose={() => setError(null)}
                    sx={{ 
                      mb: 2,
                      borderRadius: 2,
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      borderColor: 'error.main'
                    }}
                  >
                    {error}
                  </Alert>
                </Fade>
              )}
            </Paper>
          </Zoom>

          <Grid container spacing={4}>
            {/* Left Side - Data Info */}
            <Grid item xs={12} md={4}>
              <Fade in={true} timeout={1000}>
                <Card 
                  elevation={8}
                  sx={{ 
                    height: 'fit-content',
                    border: '1px solid',
                    borderColor: 'primary.light',
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,255,254,0.8) 100%)',
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                      <PersonIcon 
                        sx={{ 
                          color: 'primary.main',
                          fontSize: 28,
                          mr: 1.5
                        }} 
                      />
                      <Typography 
                        variant="h6" 
                        fontWeight={700}
                        sx={{
                          background: 'linear-gradient(135deg, #1dc39f 0%, #14a085 100%)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        Detail Longwis
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {[
                        { icon: PersonIcon, label: 'Nama PIC', value: data.nama_pic, color: 'primary.light' },
                        { icon: PhoneIcon, label: 'Kontak PIC', value: data.kontak_pic, color: 'secondary.light' },
                        { icon: LocationIcon, label: 'Nama Longwis', value: data.nama_longwis, color: 'success.light' },
                        { icon: BusinessIcon, label: 'Kecamatan', value: data.kecamatan, color: 'warning.light' },
                        { icon: HomeIcon, label: 'Kelurahan', value: data.kelurahan, color: 'info.light' },
                      ].map((item, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                          <Avatar 
                            sx={{ 
                              bgcolor: item.color, 
                              width: 36, 
                              height: 36,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}
                          >
                            <item.icon fontSize="small" />
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ 
                                fontWeight: 500,
                                mb: 0.5
                              }}
                            >
                              {item.label}
                            </Typography>
                            <Typography 
                              variant="body1" 
                              fontWeight={600}
                              sx={{ color: 'text.primary' }}
                            >
                              {item.value}
                            </Typography>
                          </Box>
                        </Box>
                      ))}

                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5 }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: 'error.light', 
                            width: 36, 
                            height: 36,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                          }}
                        >
                          <LocationIcon fontSize="small" />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              fontWeight: 500,
                              mb: 0.5
                            }}
                          >
                            Alamat
                          </Typography>
                          <Typography 
                            variant="body1" 
                            fontWeight={600}
                            sx={{ 
                              color: 'text.primary',
                              lineHeight: 1.6
                            }}
                          >
                            {data.alamat}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: 'purple', 
                            width: 36, 
                            height: 36,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                          }}
                        >
                          <CalendarIcon fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              fontWeight: 500,
                              mb: 0.5
                            }}
                          >
                            Tanggal
                          </Typography>
                          <Typography 
                            variant="body1" 
                            fontWeight={600}
                            sx={{ color: 'text.primary' }}
                          >
                            {new Date(data.tanggal).toLocaleDateString('id-ID', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 4, borderColor: 'primary.light' }} />

                    {/* Status Badge */}
                    <Box>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 2,
                          fontWeight: 600
                        }}
                      >
                        Status Laporan
                      </Typography>
                      <Chip
                        icon={getStatusIcon(data.status)}
                        label={data.status}
                        color={getStatusColor(data.status)}
                        variant="filled"
                        sx={{ 
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          height: 40,
                          borderRadius: 3,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>

            {/* Right Side - Preview & Actions */}
            <Grid item xs={12} md={8}>
              <Fade in={true} timeout={1200}>
                <Card 
                  elevation={8}
                  sx={{ 
                    border: '1px solid',
                    borderColor: 'primary.light',
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,255,254,0.8) 100%)',
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PreviewIcon 
                          sx={{ 
                            color: 'primary.main',
                            fontSize: 28,
                            mr: 1.5
                          }} 
                        />
                        <Typography 
                          variant="h6" 
                          fontWeight={700}
                          sx={{
                            background: 'linear-gradient(135deg, #1dc39f 0%, #14a085 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                          }}
                        >
                          Preview Laporan
                        </Typography>
                      </Box>
                    </Box>

                    {/* Preview Container */}
                    <Box 
                      sx={{ 
                        border: '2px dashed',
                        borderColor: 'primary.light',
                        borderRadius: 3,
                        p: 3,
                        mb: 4,
                        minHeight: '450px',
                        maxHeight: '550px',
                        overflow: 'auto',
                        background: 'linear-gradient(135deg, rgba(248,255,254,0.5) 0%, rgba(255,255,255,0.8) 100%)',
                        position: 'relative'
                      }}
                    >
                      {previewLoading ? (
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          height: '100%',
                          gap: 3
                        }}>
                          <CircularProgress 
                            size={60} 
                            sx={{ color: 'primary.main' }}
                          />
                          <Typography variant="h6" color="text.secondary">
                            Memuat preview laporan...
                          </Typography>
                          <Box sx={{ width: '100%', maxWidth: 400 }}>
                            {[...Array(6)].map((_, i) => (
                              <Skeleton 
                                key={i}
                                variant="rectangular" 
                                height={20} 
                                sx={{ mb: 1, borderRadius: 1 }}
                              />
                            ))}
                          </Box>
                        </Box>
                      ) : (
                        <Box ref={reportTemplate}>
                          <PrintPDF data={data} />
                        </Box>
                      )}
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      flexWrap: 'wrap',
                      mb: 3
                    }}>
                      <Button
                        variant="contained"
                        onClick={handleDownload}
                        disabled={loading || previewLoading}
                        startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
                        sx={{
                          background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                          },
                          flex: { xs: '1 1 100%', sm: '1 1 auto' },
                          minHeight: 48,
                          fontWeight: 700,
                        }}
                      >
                        {loading ? 'Downloading...' : 'Download PDF'}
                      </Button>

                      <Button
                        variant="outlined"
                        onClick={handlePrint}
                        disabled={previewLoading}
                        startIcon={<PrintIcon />}
                        sx={{
                          borderColor: 'primary.main',
                          color: 'primary.main',
                          borderWidth: 2,
                          '&:hover': {
                            borderColor: 'primary.dark',
                            backgroundColor: 'primary.light',
                            color: 'white',
                            borderWidth: 2,
                          },
                          flex: { xs: '1 1 100%', sm: '1 1 auto' },
                          minHeight: 48,
                          fontWeight: 700,
                        }}
                      >
                        Print Laporan
                      </Button>

                      <Button
                        variant="outlined"
                        onClick={handlePreview}
                        disabled={previewLoading}
                        startIcon={<OpenInNewIcon />}
                        sx={{
                          borderColor: 'secondary.main',
                          color: 'secondary.main',
                          borderWidth: 2,
                          '&:hover': {
                            borderColor: 'secondary.dark',
                            backgroundColor: 'secondary.light',
                            color: 'white',
                            borderWidth: 2,
                          },
                          flex: { xs: '1 1 100%', sm: '1 1 auto' },
                          minHeight: 48,
                          fontWeight: 700,
                        }}
                      >
                        Buka di Tab Baru
                      </Button>
                    </Box>

                    {/* Info Alert */}
                    <Alert 
                      severity="info" 
                      icon={<InfoIcon />}
                      sx={{ 
                        borderRadius: 3,
                        backgroundColor: 'rgba(29, 195, 159, 0.1)',
                        borderColor: 'primary.main',
                        border: '1px solid',
                        '& .MuiAlert-icon': {
                          color: 'primary.main',
                        }
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        <strong>Panduan:</strong> Gunakan tombol "Download PDF" untuk menyimpan laporan, 
                        "Print Laporan" untuk mencetak langsung, atau "Buka di Tab Baru" untuk melihat dalam halaman terpisah.
                        Pastikan browser mengizinkan pop-up untuk situs ini.
                      </Typography>
                    </Alert>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          </Grid>

          {/* Copyright */}
          <Fade in={true} timeout={1400}>
            <Box sx={{ 
              mt: 6, 
              textAlign: 'center',
              p: 3,
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(29,195,159,0.1) 0%, rgba(255,255,255,0.1) 100%)',
            }}>
              <Copyright />
            </Box>
          </Fade>
        </Container>
      </ThemeProvider>
    </Box>
  );
};

export default DaftarLaporan;
